import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';

// Compañías de PR habilitadas para CWP categorización
const CWP_ENABLED_ACCOUNTS = [25, 43, 29, 34]; // Premier, Soda, Optima, Multinational

interface CWPCategorizationProps {
  policyNumber?: string;
  accountReference: number;
  disabled?: boolean;
  value: {
    cwpClosureReasonId?: number;
    cwpClosureReasonDetailId?: number;
    cwpManualDescription?: string | null;
  };
  onChange: (data: {
    cwpClosureReasonId?: number;
    cwpClosureReasonDetailId?: number;
    cwpManualDescription?: string | null;
  }) => void;
}

interface ClosureReasonOption {
  reasonId: number;
  reasonName: string;
  detailId: number;
  detailName: string;
}

/**
 * Extrae el tipo de póliza de los primeros 3 caracteres del número de póliza.
 * Ejemplos: "PAP-9526414" → "PAP", "DAP56756-00" → "DAP", "SRO-987645" → "SRO"
 */
const extractPolicyType = (policyNumber?: string): string => {
  if (!policyNumber || policyNumber.length < 3) return 'SRO';
  const prefix = policyNumber.substring(0, 3).toUpperCase();
  const validTypes = ['DAP', 'PAP', 'LAP', 'PAC', 'SRO'];
  return validTypes.includes(prefix) ? prefix : 'SRO';
};

const CWPCategorization: FunctionComponent<CWPCategorizationProps> = ({
  policyNumber,
  accountReference,
  disabled = false,
  value,
  onChange,
}): JSX.Element | null => {
  const [reasons, setReasons] = useState<ClosureReasonOption[]>([]);
  const [loading, setLoading] = useState(false);

  const policyType = useMemo(() => extractPolicyType(policyNumber), [
    policyNumber,
  ]);

  const isEnabled = CWP_ENABLED_ACCOUNTS.includes(accountReference);

  // Cargar catálogo cuando se habilita
  useEffect(() => {
    if (!isEnabled) return;

    const fetchReasons = async () => {
      setLoading(true);
      try {
        const data = await new ClaimsTrackerClient().getCWPClosureReasons(
          policyType,
        );
        setReasons(data || []);
      } catch (error) {
        console.error('Error loading CWP closure reasons:', error);
        setReasons([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReasons();
  }, [isEnabled, policyType]);

  // Categorías únicas (agrupadas), ordenadas alfabéticamente
  const categories = useMemo(() => {
    const uniqueCategories = new Map<number, string>();
    reasons.forEach((r) => {
      if (!uniqueCategories.has(r.reasonId)) {
        uniqueCategories.set(r.reasonId, r.reasonName);
      }
    });
    return Array.from(uniqueCategories.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [reasons]);

  // Subcategorías filtradas por categoría seleccionada, ordenadas alfabéticamente
  const subcategories = useMemo(() => {
    if (!value.cwpClosureReasonId) return [];
    return reasons
      .filter((r) => r.reasonId === value.cwpClosureReasonId)
      .map((r) => ({ id: r.detailId, name: r.detailName }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [reasons, value.cwpClosureReasonId]);

  // Detectar si se seleccionó "Otro" (categoría o subcategoría)
  const selectedCategory = categories.find(
    (c) => c.id === value.cwpClosureReasonId,
  );
  const selectedSubcategory = subcategories.find(
    (s) => s.id === value.cwpClosureReasonDetailId,
  );
  const isOtroSelected =
    selectedCategory?.name === 'Otro' ||
    selectedSubcategory?.name?.toLowerCase().includes('otro');

  if (!isEnabled) return null;

  return (
    <Grid
      className="claim-adjust__data--row"
      sx={{ flexDirection: 'column', gap: '15px', width: '325px' }}
    >
      <FormControl className="claim-adjust__data--row--input">
        <InputLabel id="cwp-reason-label">Motivo de cierre *</InputLabel>
        <Select
          labelId="cwp-reason-label"
          id="cwp-reason-select"
          value={value.cwpClosureReasonId || ''}
          label="Motivo de cierre *"
          disabled={disabled || loading}
          onChange={(e) => {
            const reasonId = e.target.value as number;
            const selectedCat = categories.find((c) => c.id === reasonId);
            const isOtro = selectedCat?.name === 'Otro';
            onChange({
              cwpClosureReasonId: reasonId,
              cwpClosureReasonDetailId: undefined,
              cwpManualDescription: isOtro ? '' : undefined,
            });
          }}
        >
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>
              {cat.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl className="claim-adjust__data--row--input">
        <InputLabel id="cwp-detail-label">Detalle *</InputLabel>
        <Select
          labelId="cwp-detail-label"
          id="cwp-detail-select"
          value={value.cwpClosureReasonDetailId || ''}
          label="Detalle *"
          disabled={disabled || loading || !value.cwpClosureReasonId}
          onChange={(e) => {
            const detailId = e.target.value as number;
            const selectedSub = subcategories.find((s) => s.id === detailId);
            const isOtroDetail = selectedSub?.name
              ?.toLowerCase()
              .includes('otro');
            const isOtroCat = selectedCategory?.name === 'Otro';
            onChange({
              ...value,
              cwpClosureReasonDetailId: detailId,
              cwpManualDescription: isOtroDetail || isOtroCat ? '' : undefined,
            });
          }}
        >
          {subcategories.map((sub) => (
            <MenuItem key={sub.id} value={sub.id}>
              {sub.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isOtroSelected && (
        <TextField
          className="claim-adjust__data--row--input"
          label="Describe el motivo de cierre *"
          value={value.cwpManualDescription || ''}
          disabled={disabled}
          inputProps={{ maxLength: 100 }}
          helperText={`${(value.cwpManualDescription || '').length}/100`}
          onChange={(e) => {
            onChange({
              ...value,
              cwpManualDescription: e.target.value,
            });
          }}
        />
      )}
    </Grid>
  );
};

export { CWP_ENABLED_ACCOUNTS, extractPolicyType };
export default CWPCategorization;
